import { useNavigate, useParams, useSearchParams } from "react-router";
import { useDotLegalSnackbar, useHistoryWithReferer } from "@dotlegal/dotlegal-ui-components";
import { put } from "@common/api/apiShared";
import { StepModel } from "@common/components/dotLegalStepper/DotLegalCoreStepper.types";
import { useOptimisticUpdate } from "@common/hooks/useOptimisticUpdate";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";
import { EntityTemplate } from "../../entityTemplates/EntityTemplate.types";
import { EditLegalEntityModel, EditLegalEntityUpdateModel } from "./EditLegalEntity.types";
import { useValidateLegalEntityStep } from "./EditLegalEntityValidator.hooks";
import { useEditLegalEntitySteps } from "./useEditLegalEntitySteps";
import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { isValidInternalPath } from "../../auth/internalPath";
import { useUrlProvider } from "../../useUrlProvider";
import { CreateLegalEntityReturnType } from "../legalEntities/LegalEntities.types";
import { IEditLegalEntity } from "./EditLegalEntity";

export function useEditLegalEntity(props: IEditLegalEntity) {
    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const queryClient = useDotLegalQueryClient();
    const { id } = useParams<{ id: string }>();

    const navigate = useNavigate();
    const historyWithReferer = useHistoryWithReferer();
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { getLegalEntityBaseUrl } = useUrlProvider();

    // Creating a legal entity from an asset, a contract or a processing activity detours through this page, so
    // it has to offer the way back that the caller sent along rather than stranding the user on the entity.
    const [searchParams] = useSearchParams();
    const returnUrl = searchParams.get("returnUrl");
    const returnName = searchParams.get("returnName");
    const returnType = searchParams.get("returnType") as CreateLegalEntityReturnType | null;
    const hasValidReturnUrl = isValidInternalPath(returnUrl);

    const queryKey = getEditLegalEntityQueryKey(id);
    const { data: legalEntity, isLoading, refetch: refetchLegalEntity } = useDotLegalQueryWithKey<EditLegalEntityModel>(queryKey, getEditLegalEntityApiUrl(id));

    const entityTemplateQuery = useDotLegalQuery<EntityTemplate>(`/entity-templates/${legalEntity?.entityTemplateId}`, {
        enabled: !!legalEntity,
    });

    const { validateStep, selectedTags } = useValidateLegalEntityStep(id ?? "");

    function getTemplateSteps() {
        const steps: Array<StepModel> = [];

        if (entityTemplateQuery.data) {
            [...entityTemplateQuery.data.steps]
                .sort((a, b) => a.order - b.order)
                .forEach((step) => {
                    steps.push(new StepModel(step.name, step.order, () => validateStep(step, legalEntity!)));
                });
        }

        return steps;
    }

    const steps = useEditLegalEntitySteps(legalEntity ?? new EditLegalEntityModel(), entityTemplateQuery.data, getTemplateSteps(), selectedTags);

    async function onChange(changeFunc: (legalEntity: EditLegalEntityModel) => void) {
        const tempLegalEntity = { ...legalEntity! };
        changeFunc(tempLegalEntity);
        await putOnQueueAndSetQueryData(tempLegalEntity, queryKey, updateLegalEntityAPI, tempLegalEntity);
    }

    // Custom fields and sub contractors persist through their own endpoints as they change, so the page only
    // brings the cache back in step with what those endpoints already stored.
    function onCachedChange(changeFunc: (legalEntity: EditLegalEntityModel) => void) {
        const tempLegalEntity = { ...legalEntity! };
        changeFunc(tempLegalEntity);
        queryClient.setQueryData(queryKey, tempLegalEntity);
    }

    function onComplete() {
        if (hasValidReturnUrl) {
            snackbar.show(translateString("legalEntityCreationCompleted"));
            navigate(returnUrl!);
            return;
        }

        historyWithReferer.push(getLegalEntityBaseUrl(props.legalEntityScope, id!));
    }

    function getReturnBannerTranslationKey() {
        if (!hasValidReturnUrl || isNullOrWhitespace(returnName)) {
            return undefined;
        }

        switch (returnType) {
            case CreateLegalEntityReturnType.Asset:
                return "createLegalEntityFromAssetInfo";
            case CreateLegalEntityReturnType.Contract:
                return "createLegalEntityFromContractInfo";
            case CreateLegalEntityReturnType.ProcessingActivity:
                return "createLegalEntityFromProcessingActivityInfo";
            default:
                return undefined;
        }
    }

    return {
        isLoading: isLoading || entityTemplateQuery.isLoading,
        legalEntity: legalEntity!,
        legalEntityTemplate: entityTemplateQuery.data,
        steps: steps.getSteps(),
        activeStep: steps.getActiveStep(),
        changeStep: steps.changeStep,
        validationErrors: steps.validationErrors,
        onChange,
        onCachedChange,
        legalEntityId: id,
        refetchLegalEntity,
        onComplete,
        returnBannerTranslationKey: getReturnBannerTranslationKey(),
        returnName,
        abortReturnText: hasValidReturnUrl ? translateString("abortAndReturn") : undefined,
        onAbortReturn: hasValidReturnUrl ? () => navigate(returnUrl!) : undefined,
    };

    async function updateLegalEntityAPI(viewModel: EditLegalEntityModel) {
        return await put<EditLegalEntityUpdateModel>(getEditLegalEntityApiUrl(id), convertToUpdateModel(viewModel));
    }

    function convertToUpdateModel(viewModel: EditLegalEntityModel): EditLegalEntityUpdateModel {
        return {
            name: viewModel.name,
            description: viewModel.description,
            referenceNumber: viewModel.referenceNumber,
            note: viewModel.note,
            address: viewModel.address,
            zipCode: viewModel.zipCode,
            city: viewModel.city,
            country: viewModel.country,
            companyRegistrationNumber: viewModel.companyRegistrationNumber,
            website: viewModel.website,
            types: viewModel.types,
            isApproved: viewModel.isApproved,
            isActive: viewModel.isActive,
            responsibleId: viewModel.responsibleId,
            businessAreaIds: viewModel.businessAreaIds,
            complianceAreaIds: viewModel.complianceAreaIds,
            certificationIds: viewModel.certificationIds,
            auditType: viewModel.auditType,
            auditFrequency: viewModel.auditFrequency,
            shouldAutoAddSubContractors: viewModel.shouldAutoAddSubContractors,
        };
    }
}

export function getEditLegalEntityQueryKey(legalEntityId: string | undefined) {
    return "editLegalEntity" + legalEntityId;
}

export function getEditLegalEntityApiUrl(legalEntityId: string | undefined) {
    return `/legal-entities/${legalEntityId}/edit`;
}
